import log from 'loglevel';
import { DefaultResponseBody } from '../../browser-utilities/request-manager';
import { BrowserNotificationData, BrowserNotificationEvent } from './browser-notification.model';
import { CommandQueue } from '../../command-queue/command-queue';
import { TriggerManager } from '../../trigger-manager/trigger-manager';

/**
* Will trigger a browser notification if enabled in configuration, request and notifications are defined.
* @param responseBody
*/
export function browserNotificationListener(responseBody: DefaultResponseBody) {
  if (
    responseBody.browserNotificationEnabled &&
    responseBody.notifications
  ) {
    BrowserNotificationManager.triggerBrowserNotification(responseBody.notifications);
  };
}

class BrowserNotificationManager {

  static readonly NOT_SUPPORTED_MSG = 'Browser Notification are not supported by this browser.';

  static triggerBrowserNotification(notifications: BrowserNotificationData[]): void {
    if ((Array.isArray(notifications) && !notifications.length)) {
      log.error('Browser Notification: Format error:', notifications);
      return;
    };
  
    log.debug('Browser Notification triggered: ', notifications);
  
    if (!('Notification' in window)) {
      log.warn(this.NOT_SUPPORTED_MSG);
      return;
    };
  
    switch(Notification.permission) {
      case 'denied': 
        log.debug('Browser Notification: User Permission denied.');
        break;
      case 'granted':
        log.debug('Browser Notification: User Permission granted.');
        this.createNotifications(notifications);
        break;
      default: 
        log.debug('Browser Notification: Requesting user permission.');
        this.requestUserPermission(notifications);
    };
  }

  private static createNotifications(notifications: BrowserNotificationData[]) {
    notifications.forEach(({ title, body, iconUrl, targetUrl, campaignId, campaignName, channelConfigId, storyId }) => {
      let config = { body } as any;
      if (iconUrl) {
        config.icon = iconUrl;
      }
      try {
        this.addEventHandlers(
          new Notification(title, config), campaignId, campaignName, targetUrl, channelConfigId, storyId
        )
      } catch(error) {
        log.warn(this.NOT_SUPPORTED_MSG);
      }
    });
  }

  private static requestUserPermission(notifications: BrowserNotificationData[]) {
    // Note: callback is officially deprecated but still used in safari.
    if(this.checkNotificationPromise()) {
      Notification.requestPermission()
      .then((permission) => {
        this.handlePermission(permission, notifications);
      });
    } else {
      Notification.requestPermission((permission) => {
        this.handlePermission(permission, notifications);
      });
    };
  }

  private static handlePermission(permission: string, notifications: BrowserNotificationData[]) {
    // permission granted
    if (permission === 'granted') {
      CommandQueue.add(() => TriggerManager.eventRequest<{event: 'web-notification.accept'}>('track', {
        event: 'web-notification.accept'
      }));
      this.triggerBrowserNotification(notifications);
    } else {
      // permission declined
      CommandQueue.add(() => TriggerManager.eventRequest<{event: 'web-notification.decline'}>('track', {
        event: 'web-notification.decline'
      }));
    }
  }

  private static addEventHandlers(notification: Notification, campaignId: number | null, campaignName: string, targetUrl: string, channelConfigId: number, storyId: number | null) {
    // all the event-handlers
    notification.onshow = () => {
      CommandQueue.add(() => TriggerManager.eventRequest<BrowserNotificationEvent>('track', {
        event: 'web-notification.show',
        campaignId,
        campaignName,
        channelConfigId,
        storyId
      }));
    }

    notification.onclick = () => {
      CommandQueue.add(() => TriggerManager.eventRequest<BrowserNotificationEvent>('track', {
        event: 'web-notification.click',
        campaignId,
        campaignName,
        channelConfigId,
        storyId
      }));
  
      if (targetUrl) {
        // ensures that all other commands are executed before the redirect happens, acceptable delay?
        CommandQueue.add(() => {
          window.location = targetUrl as any;
        });
      }
    };
  }

  /**
  * Checks if the browser supports the Promise based version of requestPermission or not.
  */
  private static checkNotificationPromise() {
    try {
      Notification.requestPermission().then();
    } catch(e) {
      return false;
    }
    return true;
  }
}
